/* eslint-disable react/no-array-index-key */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Divider, Tag, Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon, SuccessFilledIcon } from '@coursera/cds-icons';

import type { ManuallyGradedQuestion } from 'bundles/grading/api/types';
import { ActionMenu, ActionMenuGroup, ActionMenuItem } from 'bundles/grading/components/common/ActionMenu';
import type { SubmissionManager } from 'bundles/grading/components/staff-graded-assignments/SubmissionManager';
import type { ReviewPartProgress } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import { getQuestionPartId, getRubricId } from 'bundles/grading/utils/formPartUtils';
import { isManuallyGradedQuestion } from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

const tagAutoGraded = (
  <Tag variant="status" priority="secondary" color="success" icon={<SuccessFilledIcon />}>
    Auto-graded
  </Tag>
);

const tagGraded = (
  <Tag variant="status" priority="secondary" color="success" icon={<SuccessFilledIcon />}>
    Graded
  </Tag>
);

const tagOptional = (
  <Typography2 component="span" variant="bodySecondary" color="supportText">
    (optional)
  </Typography2>
);

type Props = {
  submission: SubmissionManager;
  reviewPartProgress: ReviewPartProgress;
  onClick: (questionId: string, rubricId?: string) => () => void;
};

const styles = {
  divider: css`
    padding: var(--cds-spacing-150) 0;
  `,
};

const QuestionRubricSelector = ({ submission, reviewPartProgress, onClick }: Props) => {
  const currentQuestion = submission.currentQuestion;
  const currentRubric = submission.currentRubric;

  let buttonLabel = '';
  if (currentQuestion) {
    buttonLabel = _t('Question #{questionNumber}', { questionNumber: currentQuestion.position });
    if (currentRubric) {
      buttonLabel += ` - ${_t('Rubric #{rubricNumber}', { rubricNumber: currentRubric.position })}`;
    }
  } else {
    buttonLabel = currentRubric
      ? _t('Assignment Rubric #{assignmentRubricNumber}', { assignmentRubricNumber: currentRubric.position })
      : _t('Question Summary');
  }

  const items = [];

  if (!submission.isAutoGradedOnly) {
    submission.questions.forEach((q, index) => {
      const isManual = isManuallyGradedQuestion(q);
      const qId = getQuestionPartId(q);

      const rubrics = (q as ManuallyGradedQuestion).questionReviewParts
        ? (q as ManuallyGradedQuestion).questionReviewParts || []
        : [];

      const isLast = index === submission.questions.length - 1;

      const hasGroupBefore =
        submission.questions[index - 1] &&
        (submission.questions[index - 1] as ManuallyGradedQuestion)?.questionReviewParts &&
        ((submission.questions[index - 1] as ManuallyGradedQuestion)?.questionReviewParts || []).length > 0;

      const hasRubrics = rubrics.length > 0;

      // Add divider if there is a group before and there is no children
      if (!hasRubrics && hasGroupBefore) {
        items.push(
          <li role="presentation" css={styles.divider} key={`divider-1-${index}`}>
            <Divider />
          </li>
        );
      }

      if (!isManual) {
        items.push(
          <ActionMenuItem
            key={qId}
            onClick={onClick(qId)}
            label={<span>{_t('Question #{questionNumber}', { questionNumber: q.position })}</span>}
            selected={qId === submission.currentQuestionId}
            tag={tagAutoGraded}
          />
        );
      }

      // No children
      if (isManual && !hasRubrics) {
        items.push(
          <ActionMenuItem
            key={qId}
            onClick={onClick(qId)}
            label={<span>{_t('Question #{questionNumber}', { questionNumber: q.position })}</span>}
            selected={qId === submission.currentQuestionId}
          />
        );
      }

      // add divider if there is no children and not last
      if (!hasRubrics && !isLast) {
        items.push(
          <li role="presentation" css={styles.divider} key={`divider-2-${index}`}>
            <Divider />
          </li>
        );
      }

      if (isManual && hasRubrics) {
        const rubricChildren = rubrics.map((rubric) => {
          const rId = getRubricId(rubric);
          const { isRequired, isComplete } = reviewPartProgress.partDetails[rId] || {};

          return (
            <ActionMenuItem
              key={rId}
              onClick={onClick(qId, rId)}
              label={
                <span>
                  {_t('Rubric #{rubricNumber}', { rubricNumber: rubric.position })} {!isRequired && tagOptional}
                </span>
              }
              selected={rId === submission.currentRubricId}
              tag={isComplete && tagGraded}
            />
          );
        });

        items.push(
          <ActionMenuGroup key={qId} label={_t('Question #{questionNumber}', { questionNumber: q.position })}>
            {rubricChildren}
          </ActionMenuGroup>
        );
      }
    });
  }

  if (submission.assignmentRubrics.length > 0) {
    const assignmentRubricChildren = submission.assignmentRubrics.map((rubric) => {
      const rId = getRubricId(rubric);
      const { isRequired, isComplete } = reviewPartProgress.partDetails[rId] || {};
      return (
        <ActionMenuItem
          key={rId}
          onClick={onClick('all', rId)}
          label={
            <span>
              {_t('Rubric #{rubricNumber}', { rubricNumber: rubric.position })} {!isRequired && tagOptional}
            </span>
          }
          selected={rId === submission.currentRubricId}
          tag={isComplete && tagGraded}
        />
      );
    });

    items.push(
      <ActionMenuGroup key="assignment-level" label="Assignment Rubric">
        {assignmentRubricChildren}
      </ActionMenuGroup>
    );
  }

  return (
    <div data-testid="question-rubric-selector">
      <ActionMenu
        buttonLabel={buttonLabel}
        id="question-selector"
        onOpenIcon={<ChevronUpIcon />}
        onCloseIcon={<ChevronDownIcon />}
        inheritStyle={false}
      >
        {items}
      </ActionMenu>
    </div>
  );
};

export default QuestionRubricSelector;
